export type LoaderSimOptions = {
  onProgress: (p: number) => void; // p in 0..1
  onDone?: () => void;
  pauseAt?: number;               // 0..1 (default 0.52)
  durationToPauseMs?: number;     // ms for 0 -> pauseAt
  durationToEndMs?: number;       // ms for pauseAt -> 1
  easing?: (t: number) => number; // optional easing (0..1 -> 0..1)
  /** If provided, auto-resume to 1 after this delay (ms) once paused at pauseAt */
  autoFinishDelayMs?: number;
};

export function createLoaderSimulator({
                                        onProgress,
                                        onDone,
                                        pauseAt = 0.52,
                                        durationToPauseMs = 1200,
                                        durationToEndMs = 700,
                                        easing = (t) => t,
                                        autoFinishDelayMs,
                                      }: LoaderSimOptions) {
  let rafId: number | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  // Bumped on every (re)start/cancel; a tick with a stale token stops itself,
  // so overlapping rAF loops can't pile up onProgress into an update storm.
  let runToken = 0;

  let startTime = 0;
  let from = 0;
  let to = pauseAt;
  let duration = durationToPauseMs;
  let current = 0;

  type Segment = 'toPause' | 'toEnd';
  let segment: Segment = 'toPause';

  const clearTimer = () => {
    if (timeoutId != null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const tick = (ts: number, token: number) => {
    if (token !== runToken) return; // superseded by a newer run — stop
    if (!startTime) startTime = ts;
    const t = Math.min(1, (ts - startTime) / duration);
    const eased = easing(t);
    current = from + (to - from) * eased;
    onProgress(current);

    if (t < 1) {
      rafId = requestAnimationFrame((next) => tick(next, token));
      return;
    }

    // segment finished
    rafId = null;
    from = to;
    current = from;

    if (segment === 'toPause') {
      // schedule auto-finish if requested
      if (autoFinishDelayMs != null) {
        clearTimer();
        timeoutId = setTimeout(() => {
          timeoutId = null;
          resume(); // continue to 1
        }, autoFinishDelayMs);
      }
    } else {
      // reached 1
      onDone?.();
    }
  };

  const start = () => {
    cancel();
    startTime = 0;
    segment = 'toPause';
    from = 0;
    to = pauseAt;
    duration = durationToPauseMs;
    current = 0;
    onProgress(0);
    const token = runToken;
    rafId = requestAnimationFrame((ts) => tick(ts, token));
  };

  const resume = () => {
    clearTimer();
    if (rafId) return; // already animating
    runToken++;
    startTime = 0;
    segment = 'toEnd';
    from = current;
    to = 1;
    duration = durationToEndMs;
    const token = runToken;
    rafId = requestAnimationFrame((ts) => tick(ts, token));
  };

  const reset = () => {
    cancel();
    startTime = 0;
    segment = 'toPause';
    from = 0;
    to = pauseAt;
    duration = durationToPauseMs;
    current = 0;
    onProgress(0);
  };

  const cancel = () => {
    runToken++;
    if (rafId != null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    clearTimer();
  };

  return { start, resume, reset, cancel };
}
